import React, { useEffect, useState } from 'react';

import { Button } from '@/components/Button';
import { Input } from '@/components/form/Input';
import { Loader } from '@/components/Loader';
import { ProfilePictureUpload } from '@/components/ProfilePictureUpload';

/**
 * Configuration for profile edit form types.
 * Maps type to user-facing labels, placeholders, and test IDs.
 */
const CONFIG = {
  user: {
    nameLabel: 'Display Name',
    namePlaceholder: 'Enter your name',
    nameInputName: 'displayName',
    pictureHint: 'Upload a profile picture',
    testIdPrefix: 'user-profile',
  },
  organization: {
    nameLabel: 'Organization Name',
    namePlaceholder: 'Enter organization name',
    nameInputName: 'organizationName',
    pictureHint: 'Upload a profile picture for your organization',
    testIdPrefix: 'org-profile',
  },
} as const;

export type ProfileEditFormType = keyof typeof CONFIG;

export interface ProfileEditFormProps {
  /** Profile type - determines labels, placeholders, and test IDs */
  type: ProfileEditFormType;
  /** Current profile name */
  currentName: string;
  /** Current profile picture URL */
  currentPictureUrl?: string;
  /** Callback when user uploads a new picture */
  onUploadPicture: (buffer: ArrayBuffer) => void;
  /** Callback when user deletes their picture */
  onDeletePicture: () => void;
  /** Callback when user saves a new name */
  onSaveName: (name: string) => void;
  /** Whether a picture upload is in progress */
  isUploadingPicture: boolean;
  /** Callback to close the form/modal */
  onClose?: () => void;
}

/**
 * Shared profile edit form component
 *
 * A unified form for editing user or organization profiles.
 * Supports profile picture upload/delete and name editing.
 *
 * Features:
 * - Profile picture upload with delete support
 * - Name text input with validation
 * - Loading states for both operations
 * - Disabled submit when no changes made
 *
 * Used by:
 * - UserProfileEditForm (type="user")
 * - OrgProfileEditForm (type="organization")
 *
 * @example
 * ```tsx
 * <ProfileEditForm
 *   type="user"
 *   currentName="John Doe"
 *   currentPictureUrl="https://..."
 *   onUploadPicture={handleUpload}
 *   onDeletePicture={handleDelete}
 *   onSaveName={handleSave}
 *   isUploadingPicture={false}
 *   onClose={closeModal}
 * />
 * ```
 */
export const ProfileEditForm: React.FC<ProfileEditFormProps> = ({
  type,
  currentName,
  currentPictureUrl,
  onUploadPicture,
  onDeletePicture,
  onSaveName,
  isUploadingPicture,
  onClose,
}) => {
  const cfg = CONFIG[type];

  const [name, setName] = useState(currentName);
  const [hasPictureChanged, setHasPictureChanged] = useState(false);

  // Reset form state when currentName prop changes (e.g., on rerender with new data)
  useEffect(() => {
    setName(currentName);
    setHasPictureChanged(false);
  }, [currentName]);

  // Validation: name must not be empty or whitespace-only
  const isNameValid = name.trim() !== '';
  const hasChanged = name !== currentName || hasPictureChanged;
  const isLoading = isUploadingPicture;

  // Wrapper handlers to track picture changes
  const handleUploadPicture = (buffer: ArrayBuffer) => {
    onUploadPicture(buffer);
    setHasPictureChanged(true);
  };

  const handleDeletePicture = () => {
    onDeletePicture();
    setHasPictureChanged(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!hasChanged || !isNameValid) return;

    // Optimistic close pattern - close immediately for instant feedback
    // Parent component handles API call, success/error toasts, and cache invalidation
    onClose?.();

    // Fire and forget - only call if name actually changed (not just picture)
    if (name !== currentName) {
      onSaveName(name);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="flex flex-col gap-6"
      data-testid={`${cfg.testIdPrefix}-edit-form`}
      role="form"
    >
      {/* Profile Picture Upload Section */}
      <div className="flex flex-col gap-3">
        <label className="text-sm font-semibold">Profile Picture</label>
        <div className="flex items-center gap-4">
          {isUploadingPicture ? (
            <div className="border-chariot-stroke flex size-20 flex-col items-center justify-center gap-3 rounded-lg border-[3px] border-dashed">
              <Loader isLoading type="spinner" />
            </div>
          ) : (
            <ProfilePictureUpload
              source={
                currentPictureUrl
                  ? { type: 'url', url: currentPictureUrl }
                  : { type: 'empty' }
              }
              onUpload={handleUploadPicture}
              onDelete={handleDeletePicture}
              size="md"
              shape="square"
              data-testid={`${cfg.testIdPrefix}-picture-upload`}
            />
          )}
          <span className="text-chariot-text-secondary text-sm">
            {cfg.pictureHint}
          </span>
        </div>
      </div>

      {/* Name Input */}
      <div className="flex flex-col gap-3">
        <Input
          name={cfg.nameInputName}
          label={cfg.nameLabel}
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder={cfg.namePlaceholder}
          required
          disabled={isLoading}
          data-testid={`${cfg.testIdPrefix}-name-input`}
        />
      </div>

      {/* Form Actions */}
      <div className="flex items-center justify-end gap-2 pt-2">
        <Button
          onClick={onClose}
          label="Cancel"
          styleType="secondary"
          size="sm"
          disabled={isLoading}
          data-testid={`${cfg.testIdPrefix}-cancel-button`}
        />
        <Button
          type="submit"
          label="Save"
          styleType="primary"
          size="sm"
          disabled={isUploadingPicture || !hasChanged || !isNameValid}
          data-testid={`${cfg.testIdPrefix}-save-button`}
        />
      </div>
    </form>
  );
};
